import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import useAddMembersToPermissionGroup from 'modules/permissions/hooks/useAddMembersToPermissionGroup'
import permissionGroups from 'modules/permissions/store/collections/permissionGroups'
import type PermissionGroup from 'modules/permissions/store/models/PermissionGroup'
import users from 'modules/core/store/collections/users'

interface Props {
  onClose: () => void
  permissionGroup: PermissionGroup
}

const DeleteModal = ({ onClose, permissionGroup }: Props) => {
  const [addMembers] = useAddMembersToPermissionGroup()
  const [destinationSelected, setDestinationSelected] = useState(false)

  const defaultDestination = permissionGroups.basicGroup
  if (!defaultDestination) throw new Error('Basic group not found')

  const form = useLazyRef(
    () =>
      new Form(
        {
          permissions_group_id: defaultDestination.id,
        },
        {
          permissions_group_id: 'number',
        }
      )
  ).current

  useUnsafeEffect(() => {
    // If the group has no users, there is no point in selecting a destination, so we skip the
    // selection step and go straight to the deletion confirmation.
    const permissionGroupUsers = users.filter(
      (user) => user.attributes.permissions_group_id === permissionGroup.attributes.id
    )

    setDestinationSelected(permissionGroupUsers.length === 0)
  }, [permissionGroup])

  const description = destinationSelected
    ? i18n.t('settings.permissions.delete.confirm_description')
    : i18n.t('settings.permissions.delete.description')

  const selectOptions = () => {
    return permissionGroups.models
      .filter((pg) => pg.attributes.id !== permissionGroup.attributes.id)
      .map((pg) => ({
        value: pg.attributes.id,
        label: pg.attributes.name,
      }))
  }

  const onDeleteConfirm = async () => {
    const { success } = await asyncAction(async () => {
      const selectedGroupId = parseInt(form.get('permissions_group_id').value)

      const permissionGroupUsers = users.filter(
        (user) => user.attributes.permissions_group_id === permissionGroup.attributes.id
      )

      // FIXME: This should be done implicitly (and transactionally...) by the backend when deleting
      // a group, let's make sure to fix this when we move the delete action to a GraphQL mutation.
      await addMembers({
        permissionGroupId: selectedGroupId,
        accessIds: permissionGroupUsers.map((user) => user.attributes.id),
      })

      return permissionGroup.destroy()
    })

    if (success) onClose()
  }

  const onSubmit = () => {
    if (destinationSelected) {
      onDeleteConfirm()
    } else {
      setDestinationSelected(true)
    }
  }

  return (
    <Modal
      illustration="delete-cross"
      title={i18n.t('settings.permissions.delete.title', {
        name: permissionGroup.attributes.name,
      })}
      description={description}
      onClose={onClose}
      footer={
        <>
          {!destinationSelected && (
            <Button onClick={onSubmit} type="negative">
              {i18n.t('settings.permissions.delete.submit')}
            </Button>
          )}
          {destinationSelected && (
            <Button onClick={onDeleteConfirm} type="negative">
              {i18n.t('settings.permissions.delete.submit')}
            </Button>
          )}
        </>
      }
    >
      {!destinationSelected && (
        <Select
          field={form.get('permissions_group_id')}
          label={i18n.t('settings.permissions.delete.label')}
          options={selectOptions()}
        />
      )}
    </Modal>
  )
}

export default observer(DeleteModal)
